// ===== BEGIN FILE: src/components/MyChart.tsx =====
import { useMemo, useState } from 'react';
import React from 'react';
import { Box, Typography, Button, Divider, IconButton, Tooltip, Card, Sheet } from '@mui/joy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import type { CombustionMap } from '../utils/astroEngine';
import {
  getAscendantSignAndNakshatra,
  getWholeSignCuspsSidereal,
  computeImplementedVargas,
  assignPlanetsToHouses,
  vargaToPlacementsWithDegrees,
  vargaToPlacements,
  type VargaKey
} from '../utils/astroEngine';
import NorthIndianChartVedicUMD from './NorthIndianChartVedicUMD';
import PlanetHouseSummary from './PlanetHouseSummary';
import HousePlanetInterpretations from './HousePlanetInterpretations';
import HousePredictionsTable from './HousePredictionsTable';
import LifeAreaRemediesTable from './LifeAreaRemediesTable';
import ShodashaVargaTable from './ShodashaVargaTable';
import { computeHouseInfluenceRows, type HouseInfluenceRow } from '../utils/d1HouseInfluence';
import { computeBhavaBala } from '../utils/bhavaBala';
import type { AspectEdge } from '../utils/houseScores';

type PlanetOnlyName =
  | 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn'
  | 'Rahu' | 'Ketu';

type PlanetName = PlanetOnlyName;

export type ChartBirth = {
  day: number;
  month: number;
  year: number;
  hour: number;
  min: number;
  sec?: number | null;
  lat: number;
  lon: number;
  tzone?: number | null;
};

type Props = {
  birth: ChartBirth | null;
  ascLonD1?: number | null;
  cuspsD1?: number[] | null;

  grahaLon: Partial<Record<PlanetOnlyName, { lon: number; retro: boolean }>> | null;
  combustMap: CombustionMap | null;
  debilMap: Partial<Record<PlanetOnlyName, boolean>> | null;
  exaltMap: Partial<Record<PlanetOnlyName, boolean>> | null;
  aspects?: Array<{ from: PlanetOnlyName; to?: PlanetOnlyName; toHouse?: number; kind?: string; fromHouse?: number }>;

  getPlanetColor: (planet: PlanetOnlyName) => string;

  shadbalaPct?: any; // Full Shadbala object with components
  shadbalaRatio?: any;
  ishtaKashta?: Partial<Record<PlanetName, { ishta:number; kashta:number }>>;
  ikPhala?: any;
  blendedPct?: Partial<Record<PlanetName, number>>;
  bhavaSandhiMultipliers?: Partial<Record<PlanetName, number>> | null; // Bhava sandhi multipliers (0.80-1.10) when global mode ON
  houseRows?: HouseInfluenceRow[] | null;
  aspectsD1?: AspectEdge[];
  
  // Dosha/Yoga data
  kaalSarpYoga?: {
    isPresent?: boolean;
    present?: boolean;
    type?: string;
    severity?: 'partial' | 'full';
    details?: string;
    specificType?: string;
    specificEffects?: string;
    rahuHouse?: number;
    ketuHouse?: number;
  } | null;
  mangalDosha?: {
    present: boolean;
    severity?: 'low' | 'medium' | 'high' | 'cancelled';
    houses?: number[];
  };
  sadeSati?: {
    isActive: boolean;
    currentPhase?: string;
    from?: string;
    to?: string;
  } | null;
  conjunctions?: Array<{
    planets: PlanetName[];
    house: number;
    effect?: string;
    score?: number;
  }>;
};

const OWN_SIGN_SV: Record<Exclude<PlanetOnlyName, 'Rahu' | 'Ketu'>, number[]> = {
  Sun:[5], Moon:[4], Mars:[1,8], Mercury:[3,6], Jupiter:[9,12], Venus:[2,7], Saturn:[10,11],
};
const MOOLATRIKONA_SV: Partial<Record<Exclude<PlanetOnlyName, 'Rahu' | 'Ketu'>, number>> = {
  Sun:5, Moon:4, Mars:1, Mercury:6, Jupiter:9, Venus:7, Saturn:11,
};
const EXALT_SIGN_SV: Record<PlanetOnlyName, number> = {
  Sun:1, Moon:2, Mars:10, Mercury:6, Jupiter:4, Venus:12, Saturn:7,
  Rahu:2, Ketu:8,
};
const DEBIL_SIGN_SV: Record<PlanetOnlyName, number> = {
  Sun:7, Moon:8, Mars:4,  Mercury:12, Jupiter:10, Venus:6,  Saturn:1,
  Rahu:8, Ketu:2,
};
const SIGN_LORD_SV: (PlanetOnlyName | null)[] = [
  null,'Mars','Venus','Mercury','Moon','Sun','Mercury','Venus','Mars','Jupiter','Saturn','Saturn','Jupiter'
];
const PERM_FRIENDS_SV: Record<Exclude<PlanetOnlyName, 'Rahu' | 'Ketu'>, PlanetOnlyName[]> = {
  Sun:['Moon','Mars','Jupiter'], Moon:['Sun','Mercury'], Mars:['Sun','Moon','Jupiter'],
  Mercury:['Sun','Venus'], Jupiter:['Sun','Moon','Mars'], Venus:['Mercury','Saturn'],
  Saturn:['Mercury','Venus'],
};
const PERM_ENEMIES_SV: Record<Exclude<PlanetOnlyName, 'Rahu' | 'Ketu'>, PlanetOnlyName[]> = {
  Sun:['Venus','Saturn'], Moon:[], Mars:['Mercury'], Mercury:['Moon'],
  Jupiter:['Venus'], Venus:['Sun','Moon'], Saturn:['Sun','Moon','Mars'],
};
const TEMP_FRIENDLY_OFFSETS_SV = new Set([2,3,4,10,11,12]);
const PLANET_ORDER: PlanetOnlyName[] = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'];
const offset12 = (from:number,to:number)=> ((to - from + 12) % 12) + 1;
const getLordSV = (sign:number)=> (SIGN_LORD_SV[sign] ?? 'Sun') as PlanetOnlyName;
const permRel = (p:PlanetOnlyName,l:PlanetOnlyName):'friend'|'enemy'|'neutral' => {
  if (p==='Rahu'||p==='Ketu') return 'neutral';
  const x = p as Exclude<PlanetOnlyName,'Rahu'|'Ketu'>;
  if (PERM_FRIENDS_SV[x]?.includes(l)) return 'friend';
  if (PERM_ENEMIES_SV[x]?.includes(l)) return 'enemy';
  return 'neutral';
};
const tempRel = (pHouse?:number,lHouse?:number):'friend'|'enemy'|'neutral' => {
  if (!pHouse || !lHouse) return 'neutral';
  const o1 = offset12(pHouse,lHouse);
  const o2 = offset12(lHouse,pHouse);
  const friendly = TEMP_FRIENDLY_OFFSETS_SV.has(o1) && TEMP_FRIENDLY_OFFSETS_SV.has(o2);
  return friendly ? 'friend' : 'enemy';
};
const compBonus = (perm:'friend'|'enemy'|'neutral', temp:'friend'|'enemy'|'neutral'):number => {
  const v = (perm==='friend'?1:perm==='enemy'?-1:0) + (temp==='friend'?1:temp==='enemy'?-1:0);
  if (v>= 2) return +0.75;
  if (v===1) return +0.25;
  if (v===0) return 0;
  if (v===-1) return -0.25;
  return -0.75;
};
function baselineScoreSV(
  planet:PlanetOnlyName, sign:number, planetHouse?:number, signLordHouse?:number
):number {
  if (EXALT_SIGN_SV[planet] === sign) return 2.0;
  if (DEBIL_SIGN_SV[planet] === sign) return -2.0;
  if (planet==='Rahu' || planet==='Ketu') return 0;
  if (MOOLATRIKONA_SV[planet as Exclude<PlanetOnlyName,'Rahu'|'Ketu'>] === sign) return 1.5;
  if (OWN_SIGN_SV[planet as Exclude<PlanetOnlyName,'Rahu'|'Ketu'>]?.includes(sign)) return 1.0;
  const lord = getLordSV(sign);
  const perm = permRel(planet,lord);
  const temp = tempRel(planetHouse, signLordHouse);
  return compBonus(perm,temp);
}

const VARGA_KEYS: VargaKey[] = ['D1','D2','D3','D4','D7','D9','D10','D12','D16','D20','D24','D27','D30','D40','D45','D60'];
const PLANET_LIST: PlanetOnlyName[] = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'];
type VargasData = {
  ascLonD1: number;
  cuspsD1: number[];
  ascLonD9: number;
  cuspsD9: number[];
  placementsD1: Record<PlanetOnlyName, { house: number; offsetDeg: number }>;
  placementsD9: Record<PlanetOnlyName, { house: number; offsetDeg: number }>;
  svPct: Partial<Record<PlanetOnlyName, number>>;
  retroMap?: Partial<Record<PlanetOnlyName, boolean>>;
};

function toUtcIsoFromLocal(b: ChartBirth): string {
  const msLocalAsUTC = Date.UTC(b.year, b.month - 1, b.day, b.hour, b.min, b.sec ?? 0);
  const msTrueUTC = msLocalAsUTC - Math.round((b.tzone ?? 0) * 3600_000);
  return new Date(msTrueUTC).toISOString();
}

function computeLocalVargas(
  birth: ChartBirth | null,
  grahaLon: Partial<Record<PlanetOnlyName, { lon: number; retro: boolean }>> | null,
  overrides?: { ascLonD1?: number | null; cuspsD1?: number[] | null }
): VargasData | null {
  if (!grahaLon) return null;

  let ascLonD1 =
    typeof overrides?.ascLonD1 === 'number' && Number.isFinite(overrides.ascLonD1) ? overrides.ascLonD1 : undefined;
  let cuspsD1 =
    Array.isArray(overrides?.cuspsD1) && overrides.cuspsD1.length >= 12
      ? overrides.cuspsD1.slice(0, 12).map(Number)
      : undefined;

  if ((!ascLonD1 || !cuspsD1) && !birth) {
    return null;
  }

  if (!ascLonD1 || !cuspsD1) {
    const isoUtc = toUtcIsoFromLocal(birth!);
    const asc = getAscendantSignAndNakshatra(isoUtc, birth!.lat, birth!.lon);
    ascLonD1 = asc.siderealLongitudeDeg;
    cuspsD1 = getWholeSignCuspsSidereal(ascLonD1);
  }

  if (typeof ascLonD1 !== 'number' || !Number.isFinite(ascLonD1) || !cuspsD1) {
    return null;
  }

  const longs: Record<PlanetOnlyName, number> = {} as any;
  const retroMap: Partial<Record<PlanetOnlyName, boolean>> = {};
  (Object.keys(grahaLon) as PlanetOnlyName[]).forEach((planet) => {
    const rec = grahaLon[planet];
    if (!rec) return;
    longs[planet] = rec.lon;
    retroMap[planet] = !!rec.retro;
  });

  const vargas = computeImplementedVargas(ascLonD1, longs);
  const placementsD1 = assignPlanetsToHouses(cuspsD1, longs as any);
  const placementsD9 = vargaToPlacementsWithDegrees('D9', ascLonD1, longs as any);

  const ascSignD9 =
    typeof (vargas as any)?.D9?.ascSign === 'number'
      ? (vargas as any).D9.ascSign
      : Math.floor(((ascLonD1 % 360) + 360) % 360 / 30) + 1;
  const ascDegInSignD1 = ((ascLonD1 % 30) + 30) % 30;
  const navamsaPartSize = 30 / 9;
  const navamsaIdx = Math.floor(ascDegInSignD1 / navamsaPartSize);
  const navamsaStart = navamsaIdx * navamsaPartSize;
  const navamsaFrac = navamsaPartSize ? (ascDegInSignD1 - navamsaStart) / navamsaPartSize : 0;
  const ascDegInNavamsa = Math.max(0, Math.min(29.9999, navamsaFrac * 30));
  const baseD9 = ((ascSignD9 - 1 + 12) % 12) * 30;
  const ascLonD9 = baseD9 + ascDegInNavamsa;
  const cuspsD9 = Array.from({ length: 12 }, (_, i) => (baseD9 + i * 30) % 360);

  const svPct: Partial<Record<PlanetOnlyName, number>> = {};
  PLANET_LIST.forEach((planet) => {
    let sum01 = 0;
    let count = 0;
    for (const key of VARGA_KEYS) {
      const v = (vargas as any)[key];
      if (!v) continue;
      if ((planet === 'Rahu' || planet === 'Ketu') && key !== 'D1') continue;
      const sign = v?.planetSigns?.[planet];
      if (!Number.isFinite(sign)) continue;
      const placementsV = vargaToPlacements(v);
      const planetHouse = placementsV[planet]?.house;
      const lord = getLordSV(sign as number);
      const lordHouse = placementsV[lord]?.house;
      const score = baselineScoreSV(planet, sign as number, planetHouse, lordHouse);
      const cell01 = Math.max(0, Math.min(1, (score + 2) / 4));
      sum01 += cell01;
      count += 1;
    }
    svPct[planet] = count ? Math.round((sum01 / count) * 100) : undefined;
  });

  return {
    ascLonD1,
    cuspsD1,
    ascLonD9,
    cuspsD9,
    placementsD1,
    placementsD9,
    svPct,
    retroMap,
  };
}

export type ShodashaInputs = {
  ascLonDeg: number;
  planetLongitudesDeg: Partial<Record<PlanetOnlyName, number>>;
  ayanProvider?: any
};

export function buildShodashaInputs(
  birth: ChartBirth | null,
  grahaLon: Partial<Record<PlanetOnlyName, { lon: number; retro: boolean }>> | null
): ShodashaInputs | null {
  if (!birth || !grahaLon) return null;

  const isoUtc = toUtcIsoFromLocal(birth);
  const asc = getAscendantSignAndNakshatra(isoUtc, birth.lat, birth.lon);
  const ascLonDeg = asc.siderealLongitudeDeg;

  const planetLongitudesDeg: Partial<Record<PlanetOnlyName, number>> = {};
  (Object.keys(grahaLon) as PlanetOnlyName[]).forEach((p) => {
    const rec = grahaLon[p];
    if (rec) planetLongitudesDeg[p] = rec.lon;
  });

  return { ascLonDeg, planetLongitudesDeg };
}

export function useShodashaInputs(
  birth: ChartBirth | null,
  grahaLon: Partial<Record<PlanetOnlyName, { lon: number; retro: boolean }>> | null
): ShodashaInputs | null {
  return useMemo(() => buildShodashaInputs(birth, grahaLon), [birth, grahaLon]);
}

function buildPlanetTotalsForTables(
  local: VargasData | null,
  shadbalaPct: Partial<Record<PlanetName, number>> | null | undefined,
  ishtaKashta: Partial<Record<PlanetName, { ishta:number; kashta:number }>> | null | undefined,
  blendedPct: Partial<Record<PlanetName, number>> | null | undefined,
  bhavaSandhiMultipliers?: Partial<Record<PlanetName, number>> | null
) {
  if (!local) return null;

  const planets: PlanetName[] = PLANET_LIST;
  const out: any = {};
  for (const p of planets) {
    out[p] = {
      svPct: local.svPct?.[p],
      shadbalaPct: shadbalaPct?.[p],
      ishta: ishtaKashta?.[p]?.ishta,
      kashta: ishtaKashta?.[p]?.kashta,
      blendedPct: blendedPct?.[p],
      bhavaSandhiMultiplier: bhavaSandhiMultipliers?.[p],
    };
  }
  return out as Partial<Record<PlanetName, {
    svPct?: number; shadbalaPct?: number; ishta?: number; kashta?: number; blendedPct?: number; bhavaSandhiMultiplier?: number;
  }>>;
}

const BOX_W_CM = 15;
const BOX_H_CM = 10;
const PAD_PX = 12;

const ChartSlot: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      position: 'relative',
      flex: 1,
      minHeight: 0,
      display: 'flex',
      justifyContent: 'center',
      overflow: 'hidden',
      width: '100%',
      maxWidth: '100%',
      '& > *': {
        maxWidth: '100%',
        height: 'auto',
      }
    }}
  >
    {children}
  </Box>
);

function MyChart({
  birth,
  ascLonD1: overrideAscLonD1,
  cuspsD1: overrideCuspsD1,
  grahaLon,
  combustMap,
  debilMap,
  exaltMap,
  aspects,
  getPlanetColor,
  shadbalaPct,
  shadbalaRatio,
  ishtaKashta,
  ikPhala,
  blendedPct,
  bhavaSandhiMultipliers,
  houseRows: providedHouseRows,
  aspectsD1,
  kaalSarpYoga,
  mangalDosha,
  sadeSati,
  conjunctions
}: Props) {
  const [showShodashavarga, setShowShodashavarga] = useState(false);
  const [showShadbala, setShowShadbala] = useState(false);
  const [showIshtaKashta, setShowIshtaKashta] = useState(false);
  
  // Compute simple shadbala footer for Shodashavarga table
  const shadbalaFooter = useMemo<Partial<Record<PlanetName, number>> | undefined>(() => {
    if (!shadbalaPct) return undefined;
    try {
      const out: Partial<Record<PlanetName, number>> = {};
      PLANET_ORDER.forEach(p => {
        const row = (shadbalaPct as any)[p];
        if (row && typeof row.totalPct === 'number') out[p] = row.totalPct;
      });
      return out;
    } catch {
      return undefined;
    }
  }, [shadbalaPct]);
  
  const emptyNotice = (
    <Box sx={{
      mt: 1,
      p: 1.25, borderRadius: 'sm',
      border: '1px dashed var(--joy-palette-neutral-outlinedBorder)',
      bgcolor: 'neutral.softBg'
    }}>
      No chart yet. Use <em>Save &amp; Calculate</em> on the Calculator tab to compute.
    </Box>
  );

  const local = useMemo(
    () => computeLocalVargas(
      birth,
      grahaLon,
      { ascLonD1: overrideAscLonD1 ?? undefined, cuspsD1: overrideCuspsD1 ?? undefined }
    ),
    [birth, grahaLon, overrideAscLonD1, overrideCuspsD1]
  );

  const planetTotals = useMemo(() => {
    if (!local) return null;
    return buildPlanetTotalsForTables(local, shadbalaFooter ?? null, ishtaKashta ?? null, blendedPct ?? null, bhavaSandhiMultipliers ?? null);
  }, [local, shadbalaFooter, ishtaKashta, blendedPct, bhavaSandhiMultipliers]);

  // Compute Bhava Bala (House Strength) in rupas FIRST, before houseRows needs it
  const bhavaBalaRupas = useMemo(() => {
    if (!local?.ascLonD1 || !local?.placementsD1 || !aspects || !shadbalaPct) {
      return undefined;
    }
    
    const ascSign = Math.floor(local.ascLonD1 / 30) + 1; // Convert 0-360 to 1-12
    
    // Convert Shadbala to virupas for Bhavadhipati Bala
    // shadbalaFooter values are percentages (0-100), representing ratio × 100
    // Astro-Sage uses max of 390 virupas for Bhavadhipati (half of 780 total Shadbala)
    const shadbalaVirupas: Partial<Record<PlanetName, number>> = {};
    if (shadbalaFooter) {
      for (const [planet, percentage] of Object.entries(shadbalaFooter)) {
        if (typeof percentage === 'number') {
          shadbalaVirupas[planet as PlanetName] = (percentage / 100) * 390;
        }
      }
    }
    
    const bhavaBalaRows = computeBhavaBala({
      ascendantSign: ascSign,
      planetShadbalaVirupas: shadbalaVirupas,
      planetPlacements: local.placementsD1 as any,
      aspects: aspects as any,
      planetStrengthPct: blendedPct as any,
      functionalMap: {} // TODO: Add functional classification if needed
    });
    
    // Return percentages (0-100 scale)
    const percentageArray = bhavaBalaRows.map(row => row.percentage);
    
    return percentageArray;
  }, [local, blendedPct, aspects, shadbalaPct]);

  const houseRows = useMemo<HouseInfluenceRow[]>(() => {
    if (providedHouseRows && providedHouseRows.length) {
      return providedHouseRows;
    }
    if (!local) {
      return [];
    }
    const explicit = Array.isArray(aspects)
      ? aspects.map(edge => ({
          from: edge.from,
          toHouse: typeof edge.toHouse === 'number' ? edge.toHouse : undefined,
        }))
      : undefined;
    const { rows } = computeHouseInfluenceRows({
      cusps: local.cuspsD1,
      placements: local.placementsD1 as any,
      primaryStrength: blendedPct ?? undefined,
      fallbackStrength: local.svPct ?? undefined,
      aspects: explicit,
    });
    
    return rows;
  }, [providedHouseRows, local, blendedPct, aspects, shadbalaPct]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box id="section-charts-d1-d9" sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, width: '100%', maxWidth: '100%' }}>
        <Box
          sx={{
            width: { xs: '100%', md: `${BOX_W_CM}cm` },
            minHeight: { xs: 'auto', md: `${BOX_H_CM}cm` },
            border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
            borderRadius: 'md',
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 1, sm: `${PAD_PX}px` },
            gap: { xs: 2, sm: 6 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography level="title-sm" sx={{ fontWeight: 600, lineHeight: 1 }}>
              Rāśi (D1) – Local
            </Typography>
            <Tooltip title="Your main birth chart. Shows where all planets were placed in the 12 houses at your birth moment. This is the foundation of your astrological reading - like a snapshot of the sky." placement="top" arrow variant="soft" size="sm" sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}>
              <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 18, minHeight: 18, padding: '2px' }}>
                <HelpOutlineIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {birth ? (
            <ChartSlot>
              {local ? (
                <NorthIndianChartVedicUMD
                  widthCm={BOX_W_CM}
                  heightCm={BOX_H_CM}
                  cusps={local.cuspsD1}
                  placements={local.placementsD1}
                  ascLonDeg={local.ascLonD1}
                  retroMap={local.retroMap}
                  combustMap={combustMap || undefined}
                  debilMap={debilMap || undefined}
                  exaltMap={exaltMap || undefined}
                  includeNodeSpecialAspects
                  getPlanetColor={getPlanetColor}
                />
              ) : (
                <Typography level="body-sm">Waiting for local chart data…</Typography>
              )}
            </ChartSlot>
          ) : emptyNotice}
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: `${BOX_W_CM}cm` },
            minHeight: { xs: 'auto', md: `${BOX_H_CM}cm` },
            border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
            borderRadius: 'md',
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 1, sm: `${PAD_PX}px` },
            gap: { xs: 2, sm: 6 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography level="title-sm" sx={{ fontWeight: 600, lineHeight: 1 }}>
              Navāṁsa (D9) – Local
            </Typography>
            <Tooltip title="Marriage and partnership chart. Shows your spouse's nature, marriage quality, and inner strengths. Like zooming into the 9th portion of each sign to see deeper details about relationships and spiritual growth." placement="top" arrow variant="soft" size="sm" sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}>
              <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 18, minHeight: 18, padding: '2px' }}>
                <HelpOutlineIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {birth ? (
            <ChartSlot>
              {local ? (
                <NorthIndianChartVedicUMD
                  widthCm={BOX_W_CM}
                  heightCm={BOX_H_CM}
                  cusps={local.cuspsD9}
                  placements={local.placementsD9}
                  ascLonDeg={local.ascLonD9}
                  retroMap={local.retroMap}
                  combustMap={combustMap || undefined}
                  debilMap={debilMap || undefined}
                  exaltMap={exaltMap || undefined}
                  includeNodeSpecialAspects
                  getPlanetColor={getPlanetColor}
                />
              ) : (
                <Typography level="body-sm">Waiting for local chart data…</Typography>
              )}
            </ChartSlot>
          ) : emptyNotice}
        </Box>
      </Box>

      {/* Shodashavarga, Shadbala, Ishta/Kashta Tables - Show/Hide Buttons */}
      {local && grahaLon ? (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                variant="soft"
                color="primary"
                onClick={() => setShowShodashavarga(!showShodashavarga)}
              >
                {showShodashavarga ? 'Hide' : 'Show'} Shodashavarga
              </Button>
              <Tooltip title="16 divisional charts table showing planet placements across D1 through D60. Each division focuses on a specific life area (D2=wealth, D9=marriage, D10=career, D30=troubles). Higher scores mean stronger results in that area." placement="top" arrow variant="soft" size="sm" sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}>
                <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 18, minHeight: 18, padding: '2px' }}>
                  <HelpOutlineIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                variant="soft"
                color="primary"
                onClick={() => setShowShadbala(!showShadbala)}
              >
                {showShadbala ? 'Hide' : 'Show'} Shadbala
              </Button>
              <Tooltip title="Six-fold strength system measuring each planet's power. Considers position (Sthana), direction (Dig), time (Kala), motion (Chesta), natural strength (Naisargika), and aspects received (Drik). Higher % means stronger planet that gives better results." placement="top" arrow variant="soft" size="sm" sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}>
                <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 18, minHeight: 18, padding: '2px' }}>
                  <HelpOutlineIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                variant="soft"
                color="primary"
                onClick={() => setShowIshtaKashta(!showIshtaKashta)}
              >
                {showIshtaKashta ? 'Hide' : 'Show'} Ishta/Kashta
              </Button>
              <Tooltip title="Favorable vs unfavorable strength. Ishta (good) points show beneficial results, Kashta (bad) points show challenges. Higher Ishta means easier success, higher Kashta means more obstacles. The ratio shows net outcome for each planet." placement="top" arrow variant="soft" size="sm" sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}>
                <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 18, minHeight: 18, padding: '2px' }}>
                  <HelpOutlineIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {showShodashavarga && (
            <ShodashaVargaTable
              ascLonDeg={local.ascLonD1}
              planetLongitudesDeg={Object.fromEntries(
                Object.entries(grahaLon).map(([p, rec]) => [p, rec!.lon])
              ) as any}
              showTotals
              shadbalaPct={shadbalaFooter}
              ishtaKashta={ishtaKashta}
              aspectsD1={aspectsD1}
              planetSignedPct={blendedPct}
            />
          )}
          
          {showShadbala && shadbalaPct && (
            <Box sx={{ mt: 2 }}>
              <Typography level="title-md" sx={{ mb: 1, fontFamily: '"Cinzel", "Garamond", serif' }}>Sadbala - Percentages</Typography>
              
              {/* Mobile Card View */}
              <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1.5, width: '100%', minWidth: 0 }}>
                {PLANET_ORDER.map(p => {
                  const r = shadbalaPct[p];
                  if (!r || typeof r.total !== 'number') return null;
                  return (
                    <Card key={p} variant="outlined" sx={{ p: 1.5, width: '100%', minWidth: 0, overflow: 'hidden' }}>
                      <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600 }}>
                        {p}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, fontSize: '0.875rem', minWidth: 0 }}>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Total</Typography>
                          <Typography level="body-sm" sx={{ fontWeight: 600 }}>{r.total.toFixed(1)}%</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Sthana</Typography>
                          <Typography level="body-sm">{r.sthana.toFixed(1)}%</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Dig</Typography>
                          <Typography level="body-sm">{r.dig.toFixed(1)}%</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Kala</Typography>
                          <Typography level="body-sm">{r.kala.toFixed(1)}%</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Chesta</Typography>
                          <Typography level="body-sm">{r.chesta.toFixed(1)}%</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Naisargika</Typography>
                          <Typography level="body-sm">{r.naisargika.toFixed(1)}%</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Drik</Typography>
                          <Typography level="body-sm">{r.drik.toFixed(1)}%</Typography>
                        </Box>
                      </Box>
                    </Card>
                  );
                })}
              </Box>
              
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                        <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #ddd' }}>Planet</th>
                        <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #ddd' }}>Total%</th>
                        <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #ddd' }}>Sthana</th>
                        <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #ddd' }}>Dig</th>
                        <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #ddd' }}>Kala</th>
                        <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #ddd' }}>Chesta</th>
                        <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #ddd' }}>Naisargika</th>
                        <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #ddd' }}>Drik</th>
                      </tr>
                    </thead>
                    <tbody>
                    {PLANET_ORDER.map(p => {
                      const row = (shadbalaPct as any)[p];
                      if (!row || !row.components || typeof row.totalPct !== 'number') return null;
                      return (
                        <tr key={p}>
                          <td style={{ padding: '6px', borderBottom: '1px solid #eee' }}><strong>{p}</strong></td>
                          <td style={{ padding: '6px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{row.totalPct.toFixed(1)}</td>
                          <td style={{ padding: '6px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{row.components.sthana}</td>
                          <td style={{ padding: '6px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{row.components.dig}</td>
                          <td style={{ padding: '6px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{row.components.kala}</td>
                          <td style={{ padding: '6px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{row.components.cheshta}</td>
                          <td style={{ padding: '6px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{row.components.aisargika ?? row.components.naisargika}</td>
                          <td style={{ padding: '6px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{row.components.drik}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </Box>

              {shadbalaRatio && (
                <>
                  <Typography level="title-md" sx={{ mt: 2, mb: 1, fontFamily: '"Cinzel", "Garamond", serif' }}>Sadbala - Classical Ratio (virupa / minimum)</Typography>
                  <ul>
                    {PLANET_ORDER.map(p => {
                      const r = (shadbalaRatio as any)[p];
                      if (!r || typeof r.ratio !== 'number') return null;
                      return (
                        <li key={p}>
                          <strong>{p}:</strong> {r.ratio.toFixed(2)}x
                          <span style={{ opacity: 0.7 }}> &nbsp;({r.virupa} / {r.minRequired} virupa)</span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </Box>
          )}
          
          {showIshtaKashta && ikPhala && (
            <Box sx={{ mt: 2 }}>
              <Typography level="title-md" sx={{ mb: 0.5, fontFamily: '"Cinzel", "Garamond", serif' }}>
                Ista / Kasta Phala - Parasari (BPHS)
                <Typography component="span" level="body-xs" sx={{ ml: { xs: 0, sm: 2 }, display: { xs: 'block', sm: 'inline' }, fontStyle: 'italic', color: 'warning.600' }}>
                  ⚠️ Approximation mode (±10-15%)
                </Typography>
              </Typography>
              
              {/* Mobile Card View */}
              <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1.5, mt: 1, width: '100%', minWidth: 0 }}>
                {PLANET_ORDER.map(p => {
                  const r = (ikPhala as any)[p];
                  if (!r || typeof r.ucchaBala !== 'number') return null;
                  return (
                    <Card key={p} variant="outlined" sx={{ p: 1.5, width: '100%', minWidth: 0, overflow: 'hidden' }}>
                      <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600 }}>
                        {p}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, fontSize: '0.875rem', minWidth: 0 }}>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Uccha Bala</Typography>
                          <Typography level="body-sm">{r.ucchaBala.toFixed(1)}</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Chesta Bala</Typography>
                          <Typography level="body-sm">{r.cheshtaBala.toFixed(1)}</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Ista (0-60)</Typography>
                          <Typography level="body-sm" sx={{ fontWeight: 600, color: 'success.600' }}>{r.ishta.toFixed(1)}</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Kasta (0-60)</Typography>
                          <Typography level="body-sm" sx={{ fontWeight: 600, color: 'danger.600' }}>{r.kashta.toFixed(1)}</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Ista %</Typography>
                          <Typography level="body-sm" sx={{ fontWeight: 600 }}>{r.ishtaPct.toFixed(1)}%</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" sx={{ opacity: 0.7 }}>Kasta %</Typography>
                          <Typography level="body-sm" sx={{ fontWeight: 600 }}>{r.kashtaPct.toFixed(1)}%</Typography>
                        </Box>
                      </Box>
                    </Card>
                  );
                })}
              </Box>
              
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                        <th style={{textAlign:'left',padding:'6px',borderBottom:'1px solid #ddd'}}>Planet</th>
                        <th style={{textAlign:'right',padding:'6px',borderBottom:'1px solid #ddd'}}>Uccha Bala</th>
                        <th style={{textAlign:'right',padding:'6px',borderBottom:'1px solid #ddd'}}>Chesta Bala</th>
                        <th style={{textAlign:'right',padding:'6px',borderBottom:'1px solid #ddd'}}>Ista (0-60)</th>
                        <th style={{textAlign:'right',padding:'6px',borderBottom:'1px solid #ddd'}}>Kasta (0-60)</th>
                        <th style={{textAlign:'right',padding:'6px',borderBottom:'1px solid #ddd'}}>Ista%</th>
                        <th style={{textAlign:'right',padding:'6px',borderBottom:'1px solid #ddd'}}>Kasta%</th>
                      </tr>
                    </thead>
                    <tbody>
                    {PLANET_ORDER.map(p => {
                      const r = (ikPhala as any)[p];
                      if (!r || typeof r.ucchaBala !== 'number') return null;
                      return (
                        <tr key={p}>
                          <td style={{textAlign:'left',padding:'6px',borderBottom:'1px solid #eee'}}><strong>{p}</strong></td>
                          <td style={{textAlign:'right',padding:'6px',borderBottom:'1px solid #eee'}}>{r.ucchaBala.toFixed(1)}</td>
                          <td style={{textAlign:'right',padding:'6px',borderBottom:'1px solid #eee'}}>{r.cheshtaBala.toFixed(1)}</td>
                          <td style={{textAlign:'right',padding:'6px',borderBottom:'1px solid #eee'}}>{r.ishta.toFixed(1)}</td>
                          <td style={{textAlign:'right',padding:'6px',borderBottom:'1px solid #eee'}}>{r.kashta.toFixed(1)}</td>
                          <td style={{textAlign:'right',padding:'6px',borderBottom:'1px solid #eee'}}>{r.ishtaPct.toFixed(1)}</td>
                          <td style={{textAlign:'right',padding:'6px',borderBottom:'1px solid #eee'}}>{r.kashtaPct.toFixed(1)}</td>
                        </tr>
                      );
                    })}
                    </tbody>
                  </table>
                </div>
              </Box>
            </Box>
          )}
        </Box>
      ) : null}

      <Box id="section-planet-factors" sx={{ mt: 2, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
        {local && grahaLon && planetTotals && houseRows.length ? (
          <PlanetHouseSummary
            planetTotals={planetTotals as any}
            houseRows={houseRows as any}
            bhavaBalaRupas={bhavaBalaRupas}
            getPlanetColor={getPlanetColor as any}
          />
        ) : (
          emptyNotice
        )}
      </Box>

      {/* Life Area Remedies Table */}
      {local && grahaLon && houseRows?.length ? (
        <Box 
          id="section-life-areas-remedies"
          sx={{ 
          mt: 3, 
          width: '100%', 
          maxWidth: '100%', 
          overflowX: 'hidden',
          scrollMarginTop: { xs: '100px', sm: '80px' } // Prevents content from hiding behind sticky header
        }} data-section="life-areas-remedies">
          <LifeAreaRemediesTable
            houseScores={Object.fromEntries(houseRows.map(row => [row.house, row.total]))}
            placements={local.placementsD1 as any}
            houseLords={(() => {
              const lords: Record<number, PlanetName> = {};
              houseRows.forEach(row => {
                lords[row.house] = row.lord as PlanetName;
              });
              return lords;
            })()}
            houseAspects={(() => {
              const aspectMap: Record<number, string[]> = {};
              houseRows.forEach(row => {
                const aspecters = row.aspectors || [];
                aspectMap[row.house] = aspecters.map((entry: any) => entry.planet as string);
              });
              return aspectMap;
            })()}
            planetStrengths={blendedPct as any}
            getPlanetColor={getPlanetColor as any}
            onGetOverview={(lifeAreaName: string) => {
              // Dispatch custom event to open Life Area Overview modal in App.tsx
              window.dispatchEvent(new CustomEvent('openLifeAreaOverview', { 
                detail: { lifeAreaName } 
              }));
            }}
          />
        </Box>
      ) : null}

      {/* House + Planet collated interpretations from Supabase */}
      {local && grahaLon ? (
        <Box id="section-house-collation" sx={{ mt: 3, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
          <HousePlanetInterpretations
            ascLonD1={local.ascLonD1}
            cuspsD1={local.cuspsD1}
            grahaLon={grahaLon as any}
            placementsD1={local.placementsD1 as any}
            getPlanetColor={getPlanetColor as any}
            combustMap={combustMap || undefined}
            debilMap={debilMap || undefined}
            exaltMap={exaltMap || undefined}
            blendedPct={blendedPct as any}
            houseScores={
              houseRows?.length
                ? Object.fromEntries(houseRows.map(row => [row.house, row.total]))
                : undefined
            }
            kaalSarpYoga={kaalSarpYoga}
            mangalDosha={mangalDosha}
            sadeSati={sadeSati}
            conjunctions={conjunctions}
          />
        </Box>
      ) : null}

      {/* House Predictions Table with Actionable, Caution, and Remedy */}
      {local && grahaLon ? (
        <Box id="section-house-predictions" sx={{ mt: 3, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
          <HousePredictionsTable
            placementsD1={local.placementsD1 as any}
            getPlanetColor={getPlanetColor as any}
          />
        </Box>
      ) : null}
    </Box>
  );
}

// Export memoized version for performance
export default React.memo(MyChart);

// ===== END FILE: src/components/MyChart.tsx =====

// ===== END FILE: src/components/MyChart.tsx =====

